import type { VariantProps } from "class-variance-authority";

import { cn } from "@ui/lib/utils";
import { cva } from "class-variance-authority";

import { CornerMarkerDot } from "../core/CornerMarkerDot";
import { DottedBorder } from "../core/GradientBorder";
import { EGGSHELL } from "@ui/tokens/pageColors";

export type TopLineDots = "show" | "hide" | "left" | "right";

export const getSectionProps = (overrides?: {
  topPadding?: "default" | "small" | "none" | "xsmall" | "xlarge";
  bottomPadding?: "default" | "small" | "none" | "xsmall";
  horizontalPadding?: "default" | "small" | "none";
  withGap?: boolean;
  withFilledLine?: boolean;
  withCornerMarkers?: boolean;
  withSideLines?: boolean;
  withEdgeLines?: boolean;
  topLineDots?: TopLineDots;
}) => ({
  withSideLines: true,
  withFilledLine: true,
  withCornerMarkers: true,
  topLineDots: "show" as TopLineDots,
  markerBackgroundColor: EGGSHELL,
  topPadding: "default" as const,
  bottomPadding: "default" as const,
  horizontalPadding: "default" as const,
  withGap: false,
  ...overrides,
});

export const pageSectionVariants = cva("px-[var(--page-section-px)]", {
  variants: {
    narrowBody: {
      true: "mx-auto max-w-4xl",
      false: "",
    },
    horizontalPadding: {
      default: "[--page-section-px:1rem] md:[--page-section-px:3rem]",
      small: "[--page-section-px:0.5rem] md:[--page-section-px:1rem]",
      none: "[--page-section-px:0rem]",
    },
    bottomPadding: {
      default: "md:pb-120 pb-80",
      small: "md:pb-40 pb-24",
      xsmall: "pb-16",
      none: "pb-0 md:pb-0",
    },
    topPadding: {
      xlarge: "md:pt-160 pt-120",
      default: "md:pt-120 pt-80",
      small: "pt-24 md:pt-40",
      xsmall: "pt-8 md:pt-16",
      none: "pt-0",
    },
    withSideLines: {
      // Full bleed is up to the side lines
      true: "[--full-bleed:var(--page-section-px)]",
      // Full bleed is to the edge of the screen
      false: "[--full-bleed:calc(var(--page-section-px)+var(--container-full-bleed))]",
    },
  },
  defaultVariants: {
    narrowBody: false,
    horizontalPadding: "none",
    bottomPadding: "default",
    topPadding: "default",
  },
});

export default function PageSection({
  anchorId,
  borderColor = "dark",
  children,
  dark,
  title,
  topPadding = "default",
  bottomPadding = "default",
  horizontalPadding = "none",
  withSideLines = false,
  withFilledLine = false,
  withCornerMarkers = true,
  topLineDots = "show",
  withGap = true,
  withInnerGap = false,
  withEdgeLines = true,
  narrow = false,
  narrowBody = false,
  markerBackgroundColor = EGGSHELL,
}: {
  anchorId?: string;
  borderColor?: "dark" | "light";
  children: React.ReactNode;
  dark?: boolean;
  markerBackgroundColor?: null | string;
  narrow?: boolean;
  title?: string;
  withFilledLine?: boolean;
  withCornerMarkers?: boolean;
  topLineDots?: TopLineDots;
  withGap?: boolean;
  withInnerGap?: boolean;
  withSideLines?: boolean;
  withEdgeLines?: boolean;
} & VariantProps<typeof pageSectionVariants>) {
  return (
    <div
      className={cn(dark ? "bg-neutral-dark" : "", "block-section")}
      id={anchorId}
      style={{ "--marker-background-color": markerBackgroundColor } as React.CSSProperties}
    >
      <div
        className={cn(
          "container relative",
          withGap && "mt-52",
          title,
          narrow ? "max-w-4xl" : "",
          withSideLines &&
            (borderColor === "light"
              ? "border-l border-r border-[#FFFFFF24]"
              : "border-l border-r border-overlay-dark"),
        )}
      >
        {(title || withFilledLine) && (
          <div className={withInnerGap ? "pt-52" : ""}>
            {!withFilledLine && <DottedBorder className={withInnerGap ? "mt-52" : ""} color='dark' side='top' />}
            {withFilledLine && (
              <>
                {withCornerMarkers && topLineDots !== "hide" && (
                  <>
                    {(topLineDots === "show" || topLineDots === "left") && (
                      <CornerMarkerDot backgroundColor={markerBackgroundColor || "white"} position='topLeft' />
                    )}
                    {(topLineDots === "show" || topLineDots === "right") && (
                      <CornerMarkerDot backgroundColor={markerBackgroundColor || "white"} position='topRight' />
                    )}
                  </>
                )}
                <div
                  className={cn(
                    "mt-0.5 h-1 w-full",
                    borderColor === "light" ? "bg-[#FFFFFF24]" : "bg-overlay-dark",
                    withInnerGap ? "mt-52" : "",
                  )}
                />
              </>
            )}
            {/* Line from screen edge to container start */}
            {withEdgeLines && (
              <div
                className={cn(
                  "absolute top-0 h-1 w-full",
                  borderColor === "light" ? "bg-[#FFFFFF24]" : "bg-overlay-dark",
                )}
                style={{
                  width: withFilledLine ? "calc(50vw - 50%)" : "calc(50vw - 50% - 6px)",
                  left: "calc(-50vw + 50%)",
                }}
              />
            )}
            {/* Line from container end to screen edge */}
            {withEdgeLines && (
              <div
                className={cn(
                  "absolute top-0 h-1 w-full",
                  borderColor === "light" ? "bg-[#FFFFFF24]" : "bg-overlay-dark",
                )}
                style={{
                  width: withFilledLine ? "calc(50vw - 50%)" : "calc(50vw - 50% - 6px)",
                  right: "calc(-50vw + 50%)",
                }}
              />
            )}
            {title && <h2 className='f-paragraph-03 pt-16'>{title}</h2>}
          </div>
        )}
        <div
          className={pageSectionVariants({
            narrowBody,
            horizontalPadding,
            bottomPadding,
            topPadding,
            withSideLines,
          })}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
